import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * 新动画逻辑：
 * 1. 整个 section pin 住，滚动期间中间内容区向上滑动
 * 2. 中间内容完全显示后，section 取消 pin，整体随页面向上离开
 *
 * @param root       整个 section 根节点
 * @param titleEl    顶部标题区域（固定在顶部，视觉上不动）
 * @param middleEl   中间滚动内容容器（overflow hidden 的窗口）
 * @param scrollEl   实际滚动的内容（在 middleEl 内部向上移动）
 */
export default function initAnimation(
    root: HTMLElement,
    titleEl: HTMLElement,
    middleEl: HTMLElement,
    scrollEl: HTMLElement,
) {
    const setup = () => {
        const middleHeight = middleEl.getBoundingClientRect().height;
        const scrollHeight = scrollEl.getBoundingClientRect().height;
        // 需要滚动的距离：让所有内容都能显示出来
        const scrollDistance = Math.max(0, scrollHeight - middleHeight);
        // pin 的总滚动距离
        const pinDuration = scrollDistance + root.clientHeight * 0.3;

        // 只清除与当前 root 相关的 ScrollTrigger，避免影响其他组件
        ScrollTrigger.getAll()
            .filter((st) => st.trigger === root || st.pin === root)
            .forEach((st) => st.kill());

        // 初始状态：内容从底部开始
        gsap.set(scrollEl, { y: middleHeight * 0.6 });

        // Section pin + 中间内容向上滚动
        gsap.to(scrollEl, {
            y: -scrollDistance,
            ease: 'none',
            scrollTrigger: {
                trigger: root,
                start: 'top top',
                end: `+=${pinDuration}`,
                scrub: 1,
                pin: true,
                anticipatePin: 1,
            },
        });
    };

    setup();

    // resize 时重新计算
    let prevWidth = window.innerWidth;
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
        const widthChanged = window.innerWidth !== prevWidth;
        const isTouchViewport = navigator.maxTouchPoints > 0;

        if (isTouchViewport && !widthChanged) {
            return;
        }

        prevWidth = window.innerWidth;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(setup, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
        clearTimeout(resizeTimer);
        window.removeEventListener('resize', handleResize);
    };
}
